// eslint-disable-next-line no-restricted-imports
import { useEffect, useMemo, useState } from 'react';
import { useCurrentUser, useEventTracker } from '@applyboard/ui-utils';
import { CardProps } from '../../../components/ServiceCard';
import { Incentive, IncentiveServiceType } from '../../../data/incentives/useIncentives';
import { applyCardFilters } from '../../VasHub/ServiceListings/filterUtils';
import {
  FILTER_OPTIONS,
  SERVICE_CHIP_QUERY_PARAM,
  ServiceCardData,
  ServiceType,
  getCardsForService,
  getInitialSelectedChips,
} from '../../VasHub/ServiceListings/serviceConfig';
import { VAS_HUB_TRACKING_SOURCE } from '../../VasHub/constants';
import { useStudentLanguageTestCardData } from './StudentLanguageTestCards';
import {
  academicToolsCardsData,
  arrivalKitCardsData,
  bankingCardsData,
  forexCardsData,
  gicCardsData,
  housingCardsData,
  loanCardsData,
} from './StudentServiceCardsData';
import { useStudentVisaServiceCardData } from './StudentVisaServicesCardsData';

interface Filters {
  nationality?: string;
  destination?: string[];
}

interface UseStudentServiceCardsProps {
  filters: Filters;
  agencyCountry?: string;
}

export const useStudentServiceCards = ({ filters, agencyCountry }: UseStudentServiceCardsProps) => {
  const [selectedChips, setSelectedChips] = useState<string[]>(getInitialSelectedChips);

  const [currentUser] = useCurrentUser();
  const { trackEvent } = useEventTracker();

  const currentIncentivesMap = useMemo<Record<IncentiveServiceType, Incentive>>(
    () => ({}) as Record<IncentiveServiceType, Incentive>,
    [],
  );

  const currentUserId = currentUser?.id ?? '';
  const { cardsData: testCardsData, isLoading: isLoadingTestCards } =
    useStudentLanguageTestCardData({ userId: currentUserId, agencyCountry, filters });

  const { cardsData: visaCardsData, isLoading: isLoadingVisaCards } = useStudentVisaServiceCardData(
    { incentivesMap: currentIncentivesMap, filters },
  );

  const gicCards = gicCardsData(currentIncentivesMap);
  const forexCards = forexCardsData();
  const loanCards = loanCardsData(currentIncentivesMap);
  const bankingCards = bankingCardsData(currentIncentivesMap);
  const housingCards = housingCardsData();
  const arrivalKitCards = arrivalKitCardsData();
  const academicToolsCards = academicToolsCardsData();

  const filteredGicCards = applyCardFilters(gicCards, 'gic', filters);
  const filteredLoanCards = applyCardFilters(loanCards, 'loan', filters);
  const filteredBankingCards = applyCardFilters(bankingCards, 'banking', filters);
  const filteredHousingCards = applyCardFilters(housingCards, 'housing', filters);
  const filteredForexCards = applyCardFilters(forexCards, 'forex', filters);
  const filteredArrivalKitCards = applyCardFilters(arrivalKitCards, 'arrival_kits', filters);
  const filteredAcademicToolsCards = applyCardFilters(
    academicToolsCards,
    'academic tools',
    filters,
  );

  const toggleChip = (chip: string) => {
    setSelectedChips(prev => {
      if (chip === ServiceType.ALL) return [ServiceType.ALL];

      const withoutAll = prev.filter(t => t !== ServiceType.ALL);
      const isActive = withoutAll.includes(chip);
      const next = isActive ? withoutAll.filter(t => t !== chip) : [...withoutAll, chip];

      return next.length ? next : [ServiceType.ALL];
    });

    trackEvent({
      event: 'VAS - Vas Hub Page - Service Listing Chip Clicked',
      data: {
        rp_id: currentUser?.id,
        source: VAS_HUB_TRACKING_SOURCE,
        service_category: chip,
      },
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set(SERVICE_CHIP_QUERY_PARAM, selectedChips.join(','));
    const newRelativePathQuery = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newRelativePathQuery);
  }, [selectedChips]);

  const combinedCardsData = useMemo(
    () => [
      ...testCardsData,
      ...filteredLoanCards,
      ...filteredGicCards,
      ...filteredForexCards,
      ...filteredBankingCards,
      ...filteredHousingCards,
      ...filteredArrivalKitCards,
      ...filteredAcademicToolsCards,
      ...visaCardsData,
    ],
    [
      testCardsData,
      filteredLoanCards,
      filteredGicCards,
      filteredForexCards,
      filteredBankingCards,
      filteredHousingCards,
      filteredArrivalKitCards,
      filteredAcademicToolsCards,
      visaCardsData,
    ],
  );

  const selectedCardsData = useMemo(() => {
    if (selectedChips.includes(ServiceType.ALL)) return combinedCardsData;

    const cardData: ServiceCardData = {
      testCardsData,
      visaCardsData,
      filteredGicCards,
      filteredLoanCards,
      filteredBankingCards,
      filteredForexCards,
      filteredHousingCards,
      filteredArrivalKitCards,
      filteredAcademicToolsCards,
    };

    return selectedChips.reduce<CardProps[]>((acc, chip) => {
      const serviceCards = getCardsForService(chip as ServiceType, cardData);
      acc.push(...serviceCards);
      return acc;
    }, [] as CardProps[]);
  }, [
    selectedChips,
    combinedCardsData,
    testCardsData,
    filteredLoanCards,
    filteredGicCards,
    filteredForexCards,
    filteredBankingCards,
    visaCardsData,
    filteredArrivalKitCards,
    filteredHousingCards,
    filteredAcademicToolsCards,
  ]);

  const isLoadingServices = isLoadingTestCards || isLoadingVisaCards;

  return {
    selectedChips,
    toggleChip,
    filterOptions: FILTER_OPTIONS,
    selectedCardsData,
    isLoadingServices,
  } as const;
};
